import React from 'react';
import { get } from 'lodash-es';
import { setCriticalCss } from 'client/actions/critical-css';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { getModelState } from 'client/data/luckdragon/model';
import { UrlModel, URL_CONTEXT_PATH } from 'client/data/models/url';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { LeaseDealsModel, LeaseDealsPaths } from 'client/data/models/lease-deals';
import { getReduxModelStateFromStore } from 'client/data/luckdragon/redux/redux-model';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { handle404Error } from 'site-modules/shared/utils/errors';
import { getCurrentMonth, getCurrentYear } from 'site-modules/shared/utils/time-util';
import {
  MOBILE_IMAGE_WIDTH,
  DESKTOP_IMAGE_WIDTH,
  DEFAULT_JSONLD_NAME,
  DEFAULT_JSONLD_DESCRIPTION,
  DEFAULT_SEO_TITLE,
  DEFAULT_SEO_DESCRIPTION,
} from 'client/site-modules/lease-deals/constants/lease-deals-constants';
import { EdmundsCpaNoAdhesionDecorator } from 'site-modules/shared/pages/decorators/edmunds-cpa-no-adhesion';
import { LeaseDeals } from 'site-modules/lease-deals/pages/lease-deals';
import { getImageUrl, SOURCES } from 'client/utils/image-helpers';
import { PageModel } from 'client/data/models/page';
import {
  getNoVehicleImage,
  getNoVehicleImageToDisplay,
} from 'client/site-modules/shared/utils/get-no-vehicle-image-to-display';
import { decoratorParams } from 'site-modules/shared/pages/decorators/decorator-params';
import dateFormat from 'dateformat';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { getOrganizationSchema } from 'client/seo/schemas/organization';
import { VisitorModel } from 'client/data/models/visitor';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { buildContentPath, CmsModel } from 'client/data/models/cms';
import { parseContent } from 'client/data/cms/content';
import { LinkWidgetsModel } from 'client/data/models/link-widgets';
import { getCmsPathForPage } from 'site-modules/lease-deals/utils/get-cms-path-for-page';
import { interpolateTemplate } from 'site-modules/lease-deals/utils/interpolate-template';
import hiddenGemsHandful from 'client/images/hidden-gems/hidden-gems-handful.png';
import { getQuery } from 'client/utils/location';

const isLeaseDealsPreview = props => {
  return Boolean(getQuery(props?.location)?.id);
};

function buildDirectLinkMetaData(deals, photos, month) {
  const { year, make, model, payment, styleId, bodyType } = deals[0];
  const title = `${year} ${make} ${model} lease for $${payment}/mo`;
  const description = `Explore this offer and other hidden gem lease deals for ${month}. We analyze and score every new car lease to show what's truly a good deal.`;
  const photo = photos?.find(({ styleIds }) => styleIds[styleId]);
  const imageUrl = photo?.href
    ? getImageUrl(photo.href, undefined, SOURCES.MEDIA)
    : getNoVehicleImageToDisplay(bodyType);

  return { title, description, image: { url: imageUrl } };
}

const CAMPAIGNS_TO_EXCLUDE = [];

const forceDefaultExperimentsForMarketing = ({ store }) => {
  const modelState = getReduxModelStateFromStore(store);
  const utmAccountFromQuery = modelState.get('location.query.utm_account', PageModel);
  const utmAccountFromSession = modelState.get('sessionEntryParams.entryUrlParams.utm_account', VisitorModel);

  if ([utmAccountFromQuery, utmAccountFromSession].includes('edmunds_marketing')) {
    CAMPAIGNS_TO_EXCLUDE.forEach(campaign => ExperimentUtil.debug.forceRecipe(campaign, 'ctrl', store.dispatch));
  }
};

export const pageDefinitionConfig = {
  name: 'lease_deals',
  category: 'lease_deals',
  async preload(store, props) {
    const preloader = new ModelPreloader(store);
    const modelState = getReduxModelStateFromStore(store);
    const urlContext = modelState.get(URL_CONTEXT_PATH, UrlModel);

    if (urlContext && !urlContext.isValidUrl) {
      throw handle404Error({ errorMessage: 'Page not found' });
    }

    forceDefaultExperimentsForMarketing({ store });

    const {
      match: { path },
      location: { pathname },
    } = props;

    const isPreview = isLeaseDealsPreview(props);

    if (isPreview) {
      const rawId = getQuery(props.location).id;
      const id = Array.isArray(rawId) ? rawId[0] : rawId;
      store.dispatch(legacyPageUpdate({ pageName: 'lease_deals_preview' }));
      preloader.resolve(LeaseDealsPaths.getSelectedDealPath(id), LeaseDealsModel);
    }

    preloader.resolve(buildContentPath(getCmsPathForPage({ matchPath: path, pathname })), CmsModel);

    preloader.resolve(LeaseDealsPaths.getLeaseDealsPath(), LeaseDealsModel);
    preloader.resolve(LeaseDealsPaths.getFiltersPath(), LeaseDealsModel);

    const { mobile } = store.getState();

    preloader.resolve(
      LeaseDealsPaths.getPhotosPath(mobile ? MOBILE_IMAGE_WIDTH : DESKTOP_IMAGE_WIDTH),
      LeaseDealsModel
    );

    store.dispatch(setCriticalCss(false));

    await preloader.load();

    const cmsContent = parseContent(
      modelState.get(buildContentPath(getCmsPathForPage({ matchPath: path, pathname })), CmsModel)
    );

    const hasRelatedInformationLinksWidget = cmsContent.child('footer').hasChild('page-links-widget');

    if (hasRelatedInformationLinksWidget) {
      preloader.resolve('pageLinks', LinkWidgetsModel);
    }

    const leaseDealsData = modelState.get(LeaseDealsPaths.getLeaseDealsPath(), LeaseDealsModel);
    const photos = modelState.get(
      LeaseDealsPaths.getPhotosPath(mobile ? MOBILE_IMAGE_WIDTH : DESKTOP_IMAGE_WIDTH),
      LeaseDealsModel
    );

    if (leaseDealsData?.deals?.length) {
      const firstDeal = leaseDealsData.deals[0];
      const heroImage = photos?.find(photo => photo.styleIds[firstDeal.styleId]);

      const assetsToPreload = [
        {
          href: getImageUrl(
            heroImage ? heroImage.href : getNoVehicleImage(firstDeal.bodyType),
            undefined,
            SOURCES.MEDIA
          ),
          as: 'image',
        },
        { href: hiddenGemsHandful, as: 'image' },
      ];

      preloader.set('server.preloadedAssets', PageModel, assetsToPreload);
    }

    return preloader.load();
  },
  seo: (state, props) => {
    const modelState = getModelState(state);

    const urlContext = modelState.get(URL_CONTEXT_PATH, UrlModel);
    const { stateName = '', city = '' } = get(urlContext, 'location', {});
    const {
      location: { pathname },
      match: { path },
    } = props;

    const leaseDealsData = modelState.get(LeaseDealsPaths.getLeaseDealsPath(), LeaseDealsModel);
    const deals = leaseDealsData?.deals || [];

    const filtersData = modelState.get(LeaseDealsPaths.getFiltersPath(), LeaseDealsModel);

    const { mobile } = state;
    const photos = modelState.get(
      LeaseDealsPaths.getPhotosPath(mobile ? MOBILE_IMAGE_WIDTH : DESKTOP_IMAGE_WIDTH),
      LeaseDealsModel
    );

    const cmsContent = parseContent(
      modelState.get(buildContentPath(getCmsPathForPage({ matchPath: path, pathname })), CmsModel)
    );

    const make = modelState.get(LeaseDealsPaths.getRouteFiltersPath(), LeaseDealsModel)?.make;

    const currentDate = new Date();
    const currentMonth = getCurrentMonth(currentDate);
    const currentYear = getCurrentYear(currentDate);

    const variables = {
      month: currentMonth,
      year: currentYear,
      location: city || stateName,
      state: stateName,
      make: make ?? '',
    };

    const firstOfTheMonth = dateFormat(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1), 'mm/dd/yyyy');
    const publishDate = dateFormat(deals[0]?.publishDate, 'mm/dd/yyyy');

    const isPopulatedDirectLink = !!props.location?.query?.id && !!deals[0];
    const baseMeta = isPopulatedDirectLink
      ? buildDirectLinkMetaData(deals, photos, currentMonth)
      : {
          title: interpolateTemplate(cmsContent.metadata('seo-title').value(DEFAULT_SEO_TITLE), variables),
          description: interpolateTemplate(
            cmsContent.metadata('seo-description').value(DEFAULT_SEO_DESCRIPTION),
            variables
          ),
        };

    const jsonldName = interpolateTemplate(cmsContent.metadata('jsonld-name').value(DEFAULT_JSONLD_NAME), variables);
    const jsonldDescription = interpolateTemplate(
      cmsContent.metadata('jsonld-description').value(DEFAULT_JSONLD_DESCRIPTION),
      variables
    );

    return {
      ...baseMeta,
      keywords: `${currentMonth} ${currentYear} best lease deals low monthly payments${
        city || stateName ? ` in ${city || stateName}` : ''
      }`,
      jsonld: [
        {
          '@type': 'CollectionPage',
          name: jsonldName,
          description: jsonldDescription,
          url: `https://www.edmunds.com${pathname}`,
          datePublished: firstOfTheMonth,
          dateModified: publishDate,
          publisher: getOrganizationSchema(),
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: filtersData?.monthlyPayment?.min,
            highPrice: filtersData?.monthlyPayment?.max,
            priceCurrency: 'USD',
            offerCount: leaseDealsData?.topDealsByModelCount,
          },
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: deals.length,
            itemListOrder: 'Decending',
            name: `${jsonldName} ${currentYear}`,
            itemListElement: deals.map((deal, index) => {
              const photo = photos?.find(p => p.styleIds[deal.styleId]);
              const imageUrl = getImageUrl(
                photo ? photo.href : getNoVehicleImage(deal.bodyType),
                undefined,
                SOURCES.MEDIA
              );

              return {
                '@type': 'ListItem',
                position: index + 1,
                item: {
                  '@type': 'Vehicle',
                  name: `${deal.year} ${deal.make} ${deal.model}`,
                  image: imageUrl,
                  description: `Lease offer for the ${deal.year} ${deal.make} ${deal.model}: ${getPriceStringV2(
                    deal.payment
                  )}/mo for ${deal.term} months with ${getPriceStringV2(deal.dueAtSigning)} due at signing.`,
                  url: `https://www.edmunds.com${pathname}?id=${deal.id}`,
                  brand: {
                    '@type': 'Brand',
                    name: deal.make,
                  },
                  offers: {
                    '@type': 'Offer',
                    price: deal.payment,
                    priceCurrency: 'USD',
                    priceSpecification: {
                      '@type': 'UnitPriceSpecification',
                      price: deal.payment,
                      priceCurrency: 'USD',
                      billingDuration: deal.term,
                      billingIncrement: 'month',
                    },
                    availability: 'InStock',
                    availabilityStarts: firstOfTheMonth,
                    availabilityEnds: dateFormat(new Date(deal.endDate), 'mm/dd/yyyy'),
                    priceValidUntil: dateFormat(new Date(deal.endDate), 'mm/dd/yyyy'),
                  },
                },
              };
            }),
          },
        },
      ],
    };
  },
  component: props => <LeaseDeals {...props} {...(isLeaseDealsPreview(props) ? { withPreviewCard: true } : {})} />,
  decorator: decoratorParams(EdmundsCpaNoAdhesionDecorator, { className: 'lease-deals-page' }),
  options: {},
  chunkName: 'lease-deals',
};

export default pageDefinition(pageDefinitionConfig);
